/**
 * Servicio para gestionar licencias web en RTDB
 * Nodo: usersweb/{userId}
 */

import { getDatabase, ref, set, get, update, onValue } from 'firebase/database';
import { database } from '../firebase';

// Configuración de planes de suscripción
export const LICENSE_PLANS = {
    monthly: {
        id: 'monthly',
        name: 'Mensual',
        durationDays: 30,
        price: 250,
        preferenceId: '17574880-760261f8-dc4e-4c2c-8035-1e6b2b5be72b',
        description: '1 mes de acceso premium web'
    },
    biannual: {
        id: 'biannual',
        name: 'Bimestral',
        durationDays: 60,
        price: 450,
        preferenceId: '17574880-ba0a4b8f-8781-4844-82b6-e038cd6d90d8',
        description: '2 meses de acceso premium web'
    },
    semiannual: {
        id: 'semiannual',
        name: 'Semestral',
        durationDays: 180,
        price: 1100,
        preferenceId: '17574880-40e276d9-12ce-40fa-9ae3-0dd1e9b91185',
        description: '6 meses de acceso premium web'
    },
    annual: {
        id: 'annual',
        name: 'Anual',
        durationDays: 365,
        price: 1900,
        preferenceId: '17574880-687696f0-9000-4a42-a4eb-fbecac4e7668',
        description: '1 año de acceso premium web'
    }
};

/**
 * Obtener el plan por ID
 * @param {string} planId - ID del plan
 * @returns {Object} Datos del plan
 */
export const getPlanById = (planId) => {
    return LICENSE_PLANS[planId] || null;
};

/**
 * Crear o actualizar licencia web para un usuario
 * @param {string} userId - ID del usuario
 * @param {Object} licenseData - Datos de la licencia
 */
export const createWebLicense = async (userId, licenseData) => {
    try {
        const db = getDatabase();
        const licenseRef = ref(db, `usersweb/${userId}/license`);

        await set(licenseRef, {
            ...licenseData,
            createdAt: Date.now()
        });

        console.log('Licencia web creada exitosamente:', userId);
        return { success: true };
    } catch (error) {
        console.error('Error al crear licencia web:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Obtener estado actual de la licencia web
 * @param {string} userId - ID del usuario
 * @returns {Object} Estado de la licencia
 */
export const getWebLicenseStatus = async (userId) => {
    try {
        const db = getDatabase();
        const licenseRef = ref(db, `usersweb/${userId}/license`);
        const snapshot = await get(licenseRef);

        if (snapshot.exists()) {
            const data = snapshot.val();
            const now = Date.now();
            const isValid = data.isActive && data.endDate > now;

            return {
                hasLicense: true,
                isValid,
                license: data,
                daysRemaining: Math.max(0, Math.ceil((data.endDate - now) / (1000 * 60 * 60 * 24)))
            };
        }

        return { hasLicense: false, isValid: false, license: null, daysRemaining: 0 };
    } catch (error) {
        console.error('Error al obtener estado de licencia:', error);
        return { hasLicense: false, isValid: false, error: error.message };
    }
};

/**
 * Suscribirse a cambios en la licencia web en tiempo real
 * @param {string} userId - ID del usuario
 * @param {Function} callback - Función callback para actualizaciones
 * @returns {Function} Función para cancelar la suscripción
 */
export const subscribeToWebLicense = (userId, callback) => {
    const db = getDatabase();
    const licenseRef = ref(db, `usersweb/${userId}/license`);

    const unsubscribe = onValue(licenseRef, (snapshot) => {
        if (snapshot.exists()) {
            const data = snapshot.val();
            const now = Date.now();
            const isValid = data.isActive && data.endDate > now;

            callback({
                hasLicense: true,
                isValid,
                license: data,
                daysRemaining: Math.max(0, Math.ceil((data.endDate - now) / (1000 * 60 * 60 * 24)))
            });
        } else {
            callback({ hasLicense: false, isValid: false, license: null, daysRemaining: 0 });
        }
    });

    return unsubscribe;
};

/**
 * Activar licencia web manualmente (para panel de admin)
 * @param {string} userId - ID del usuario
 * @param {string} planId - ID del plan
 * @param {string} paymentId - ID del pago
 */
export const activateWebLicense = async (userId, planId, paymentId = 'manual_activation') => {
    try {
        const plan = getPlanById(planId);
        if (!plan) {
            return { success: false, error: 'Plan no encontrado' };
        }

        const now = Date.now();
        const licenseData = {
            type: planId,
            startDate: now,
            endDate: now + (plan.durationDays * 24 * 60 * 60 * 1000),
            isActive: true,
            paymentId: paymentId,
            preferenceId: plan.preferenceId,
            amount: plan.price
        };

        return await createWebLicense(userId, licenseData);
    } catch (error) {
        console.error('Error al activar licencia:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Desactivar licencia web
 * @param {string} userId - ID del usuario
 */
export const deactivateWebLicense = async (userId) => {
    try {
        const db = getDatabase();
        const licenseRef = ref(db, `usersweb/${userId}/license`);

        await update(licenseRef, { isActive: false });

        return { success: true };
    } catch (error) {
        console.error('Error al desactivar licencia:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Guardar información de usuario en usersweb
 * @param {string} userId - ID del usuario
 * @param {Object} userData - Datos del usuario
 */
export const saveWebUserData = async (userId, userData) => {
    try {
        const db = getDatabase();
        const userRef = ref(db, `usersweb/${userId}`);

        await update(userRef, {
            ...userData,
            updatedAt: Date.now()
        });

        return { success: true };
    } catch (error) {
        console.error('Error al guardar datos de usuario web:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Obtener todos los usuarios web (para panel de admin)
 * @returns {Array} Lista de usuarios web
 */
export const getAllWebUsers = async () => {
    try {
        const db = getDatabase();
        const usersRef = ref(db, 'usersweb');
        const snapshot = await get(usersRef);

        if (snapshot.exists()) {
            const data = snapshot.val();
            return Object.entries(data).map(([userId, userData]) => ({
                userId,
                ...userData
            }));
        }

        return [];
    } catch (error) {
        console.error('Error al obtener usuarios web:', error);
        return [];
    }
};

export default {
    LICENSE_PLANS,
    getPlanById,
    createWebLicense,
    getWebLicenseStatus,
    subscribeToWebLicense,
    activateWebLicense,
    deactivateWebLicense,
    saveWebUserData,
    getAllWebUsers
};
